import React, { useState } from 'react'
import { ListChecks, RefreshCw, Loader2, Check, Download, PenLine } from 'lucide-react'
import { genId } from '../lib/id'
import { fetchMyExams, loadExamAsQuiz, type MyExamItem } from '../lib/examToQuiz'
import type { QuizCard } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'

// Thẻ trắc nghiệm: nạp câu hỏi từ ĐỀ THI GV đã lưu trong "Quản lý đề thi" của PMaker.
const Editor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const c = card as QuizCard
  const [exams, setExams] = useState<MyExamItem[] | null>(null)
  const [picking, setPicking] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const openPicker = async () => {
    setPicking(true)
    setError(null)
    if (exams) return
    setLoading(true)
    try {
      setExams(await fetchMyExams())
    } catch {
      setError('Không tải được danh sách đề. Hãy đăng nhập tài khoản giáo viên (PRO).')
    } finally {
      setLoading(false)
    }
  }

  const choose = async (ex: MyExamItem) => {
    setLoading(true)
    setError(null)
    try {
      const { title, questions } = await loadExamAsQuiz(ex.slug)
      if (!questions.length) {
        setError('Đề này chưa có câu hỏi đọc được.')
        return
      }
      onChange({ source: 'pmaker', bankRef: ex.slug, questions, title: c.title || title } as Partial<QuizCard>)
      setPicking(false)
    } catch {
      setError('Không nạp được đề thi.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-3">
      {/* Nguồn đề */}
      <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm text-indigo-800">
            {c.questions?.length ? (
              <>Đã nạp <strong>{c.questions.length}</strong> câu{c.bankRef ? <> từ đề <code className="text-[11px]">{c.bankRef}</code></> : ''}.</>
            ) : (
              <>Lấy câu hỏi từ <strong>đề thi đã lưu</strong> (PMaker → Quản lý đề thi).</>
            )}
          </div>
          <button
            onClick={openPicker}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700"
          >
            {c.questions?.length ? <><RefreshCw size={13} /> Đổi đề</> : <><Download size={13} /> Chọn đề</>}
          </button>
        </div>
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      </div>

      {/* Danh sách đề để chọn */}
      {picking && (
        <div className="rounded-lg border border-gray-200 max-h-64 overflow-auto">
          {loading && !exams ? (
            <div className="flex items-center justify-center py-8 text-indigo-500"><Loader2 className="animate-spin" /></div>
          ) : exams && exams.length === 0 ? (
            <p className="p-4 text-center text-sm text-gray-400">Chưa có đề thi nào đã lưu.</p>
          ) : (
            (exams || []).map((ex) => {
              const on = c.bankRef === ex.slug
              return (
                <button
                  key={ex.id}
                  onClick={() => choose(ex)}
                  disabled={loading}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2.5 border-b border-gray-50 hover:bg-indigo-50/60 disabled:opacity-50 ${on ? 'bg-indigo-50' : ''}`}
                >
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-gray-800 truncate">{ex.title}</span>
                    <span className="block text-[11px] text-gray-400">
                      {ex.is_published ? 'Đã xuất bản' : 'Nháp'}{ex.created_at ? ` · ${new Date(ex.created_at).toLocaleDateString('vi-VN')}` : ''}
                    </span>
                  </span>
                  {on && <Check size={16} className="text-indigo-600 shrink-0" />}
                </button>
              )
            })
          )}
        </div>
      )}

      {/* Cấu hình làm bài */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-bold text-gray-500 uppercase">Thời gian (giây)</label>
          <input
            type="number"
            className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
            value={c.timeLimit ?? ''}
            onChange={(e) => onChange({ timeLimit: e.target.value ? Number(e.target.value) : undefined })}
          />
        </div>
        <div>
          <label className="text-xs font-bold text-gray-500 uppercase">Điểm đạt (%)</label>
          <input
            type="number"
            className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
            value={c.passingScore ?? ''}
            onChange={(e) => onChange({ passingScore: e.target.value ? Number(e.target.value) : undefined })}
          />
        </div>
      </div>
    </div>
  )
}

const Preview: React.FC<CardPreviewProps> = ({ card }) => {
  const c = card as QuizCard
  const count = c.questions?.length || 0
  if (!count && !c.bankRef) {
    return (
      <div className="p-4 rounded-lg bg-gray-50 border border-dashed border-gray-200 text-gray-400 text-sm text-center">
        Chưa chọn đề. Bấm <strong>Chọn đề</strong> để lấy từ Quản lý đề thi (PMaker).
      </div>
    )
  }
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-indigo-200 bg-indigo-50/60">
      <span className="w-11 h-11 rounded-xl bg-indigo-500 text-white flex items-center justify-center shrink-0">
        <ListChecks size={22} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[11px] font-black uppercase tracking-wide text-indigo-600">Bài trắc nghiệm</span>
        <span className="block text-base font-bold text-gray-800 truncate">{c.title || 'Bài kiểm tra'}</span>
        <span className="block text-xs text-gray-400">
          {count ? `${count} câu` : ''}{c.timeLimit ? ` · ${c.timeLimit}s` : ''}{c.passingScore ? ` · đạt ${c.passingScore}%` : ''}
        </span>
      </span>
      {c.bankRef ? (
        <a
          href={`/exam/${c.bankRef}`}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700"
        >
          <PenLine size={16} /> Làm bài
        </a>
      ) : (
        <span className="shrink-0 text-xs text-gray-400">Cần chọn đề để làm bài</span>
      )}
    </div>
  )
}

export const quizCardDef: CardDef = {
  type: 'quiz',
  label: 'Trắc nghiệm',
  description: 'Lấy đề từ Quản lý đề thi (PMaker)',
  icon: ListChecks,
  color: 'bg-indigo-50 text-indigo-600',
  createDefault: (): QuizCard => ({ id: genId(), type: 'quiz', source: 'pmaker', questions: [] }),
  Editor,
  Preview,
}
